import { useState, useRef, useEffect } from 'react'
import { X, Folder, ChevronDown, Check, Link } from 'lucide-react'
import { useTheme } from '../../hooks/useTheme'

export interface Project {
  id: string
  name: string
  instructions?: string
  connectors?: string[]
  createdAt: Date
}

interface CreateProjectModalProps {
  onClose: () => void
  onCreate: (project: Omit<Project, 'id' | 'createdAt'>) => void
  connectorStatuses?: Record<string, string>
}

const CONNECTOR_LIST: Array<{ id: string; label: string; icon: string }> = [
  { id: 'gmail',    label: 'Gmail',           icon: '📧' },
  { id: 'github',   label: 'GitHub',          icon: '🐙' },
  { id: 'calendar', label: 'Google Calendar', icon: '📅' },
  { id: 'slack',    label: 'Slack',           icon: '💬' },
  { id: 'notion',   label: 'Notion',          icon: '📝' },
  { id: 'meta',     label: 'Meta Ads',        icon: '📣' },
]

export function CreateProjectModal({ onClose, onCreate, connectorStatuses = {} }: CreateProjectModalProps) {
  const { isDark } = useTheme()
  const [name, setName] = useState('')
  const [instructions, setInstructions] = useState('')
  const [selectedConnectors, setSelectedConnectors] = useState<string[]>([])
  const [showConnectorPicker, setShowConnectorPicker] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!showConnectorPicker) return
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowConnectorPicker(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showConnectorPicker])

  const handleCreate = () => {
    if (!name.trim()) return
    onCreate({
      name: name.trim(),
      instructions: instructions.trim() || undefined,
      connectors: selectedConnectors,
    })
    onClose()
  }

  const toggleConnector = (id: string) => {
    setSelectedConnectors(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    )
  }

  // Colors based on theme
  const bg = isDark ? '#1c1c1e' : '#FFFFFF'
  const border = isDark ? '#3a3a3c' : '#E5E5E5'
  const inputBg = isDark ? '#2c2c2e' : '#F5F5F5'
  const inputBorder = isDark ? '#3a3a3c' : '#E0E0E0'
  const inputBorderFocus = isDark ? '#5A5A5E' : '#AAAAAA'
  const textPrimary = isDark ? '#F0F0F0' : '#1A1A1A'
  const textSecondary = isDark ? '#E0E0E0' : '#3A3A3A'
  const textMuted = isDark ? '#7A7A7A' : '#9A9A9A'
  const iconBg = isDark ? '#2c2c2e' : '#F0F0F0'
  const dropdownBg = isDark ? '#2c2c2e' : '#FFFFFF'
  const dropdownItemHover = isDark ? '#333336' : '#F5F5F5'
  const dropdownItemActive = isDark ? '#3a3a3e' : '#EBF3FF'
  const cancelBorder = isDark ? '#3A3A3C' : '#E0E0E0'
  const cancelColor = isDark ? '#C0C0C0' : '#6A6A6A'
  const cancelHover = isDark ? '#2a2a2c' : '#F0F0F0'
  const createBg = isDark ? '#3a3a3e' : '#1A1A1A'
  const createBgDisabled = isDark ? '#2a2a2c' : '#E5E5E5'
  const createColor = isDark ? '#F0F0F0' : '#FFFFFF'
  const createColorDisabled = isDark ? '#6A6A6A' : '#AAAAAA'
  const createHover = isDark ? '#4a4a4e' : '#333333'
  const overlayBg = isDark ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.4)'

  const connectorCount = selectedConnectors.length
  const connectorLabel = connectorCount > 0
    ? `${connectorCount} connector${connectorCount > 1 ? 's' : ''} valgt`
    : 'Connectors (valgfritt)'

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: overlayBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: bg,
          border: `1px solid ${border}`,
          borderRadius: 16,
          padding: '28px 28px 24px',
          width: '100%',
          maxWidth: 520,
          boxShadow: isDark
            ? '0 24px 80px rgba(0,0,0,0.6)'
            : '0 24px 80px rgba(0,0,0,0.15)',
          position: 'relative',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: 16, right: 16,
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: textMuted, display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 28, height: 28, borderRadius: 8,
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => (e.currentTarget.style.background = isDark ? '#2a2a2c' : '#F0F0F0')}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
        >
          <X size={16} color={textMuted} />
        </button>

        {/* Title */}
        <h2 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 600, color: textPrimary }}>
          Opprett prosjekt
        </h2>

        {/* Folder icon */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
          <div style={{
            background: iconBg,
            borderRadius: 14,
            width: 64, height: 64,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Folder size={28} color={isDark ? '#A0A0A0' : '#6A6A6A'} />
          </div>
        </div>

        {/* Project name */}
        <div style={{ marginBottom: 18 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: textSecondary, marginBottom: 8 }}>
            Prosjektnavn
          </label>
          <input
            autoFocus
            type="text"
            placeholder="Skriv inn navn"
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') onClose() }}
            style={{
              width: '100%',
              background: inputBg,
              border: `1px solid ${inputBorder}`,
              borderRadius: 10,
              padding: '10px 14px',
              fontSize: 14,
              color: textPrimary,
              outline: 'none',
              boxSizing: 'border-box',
              transition: 'border-color 0.15s',
              fontFamily: 'inherit',
            }}
            onFocus={e => (e.currentTarget.style.borderColor = inputBorderFocus)}
            onBlur={e => (e.currentTarget.style.borderColor = inputBorder)}
          />
        </div>

        {/* Instructions */}
        <div style={{ marginBottom: 18 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: textSecondary, marginBottom: 8 }}>
            Instruksjoner{' '}
            <span style={{ fontWeight: 400, color: textMuted }}>(valgfritt)</span>
          </label>
          <textarea
            placeholder={`F.eks. "Fokuser på Python best practices", "Hold en profesjonell tone", eller\n"Gi alltid kilder til viktige konklusjoner".`}
            value={instructions}
            onChange={e => setInstructions(e.target.value)}
            rows={4}
            style={{
              width: '100%',
              background: inputBg,
              border: `1px solid ${inputBorder}`,
              borderRadius: 10,
              padding: '10px 14px',
              fontSize: 13,
              color: textSecondary,
              outline: 'none',
              resize: 'vertical',
              boxSizing: 'border-box',
              lineHeight: 1.6,
              transition: 'border-color 0.15s',
              fontFamily: 'inherit',
            }}
            onFocus={e => (e.currentTarget.style.borderColor = inputBorderFocus)}
            onBlur={e => (e.currentTarget.style.borderColor = inputBorder)}
          />
          <p style={{ margin: '6px 0 0', fontSize: 11, color: textMuted, lineHeight: 1.4 }}>
            Instruksjonene gjelder for alle samtaler i dette prosjektet.
          </p>
        </div>

        {/* Connectors */}
        <div style={{ marginBottom: 24, position: 'relative' }} ref={dropdownRef}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: textSecondary, marginBottom: 8 }}>
            Connectors{' '}
            <span style={{ fontWeight: 400, color: textMuted }}>(valgfritt)</span>
          </label>
          <button
            onClick={() => setShowConnectorPicker(v => !v)}
            style={{
              width: '100%',
              background: inputBg,
              border: `1px solid ${showConnectorPicker ? inputBorderFocus : inputBorder}`,
              borderRadius: 10,
              padding: '10px 14px',
              fontSize: 13,
              color: connectorCount > 0 ? textSecondary : textMuted,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontFamily: 'inherit',
              transition: 'border-color 0.15s',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Link size={13} color={textMuted} />
              {connectorLabel}
            </span>
            <ChevronDown
              size={14}
              color={textMuted}
              style={{ transform: showConnectorPicker ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
            />
          </button>

          {/* Connector picker dropdown */}
          {showConnectorPicker && (
            <div
              style={{
                position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
                background: dropdownBg,
                border: `1px solid ${border}`,
                borderRadius: 10,
                padding: '6px',
                zIndex: 10,
                boxShadow: isDark
                  ? '0 8px 24px rgba(0,0,0,0.4)'
                  : '0 8px 24px rgba(0,0,0,0.12)',
              }}
            >
              {CONNECTOR_LIST.map(c => {
                const isSelected = selectedConnectors.includes(c.id)
                const isConnected = connectorStatuses[c.id] === 'connected'
                return (
                  <button
                    key={c.id}
                    onClick={() => toggleConnector(c.id)}
                    style={{
                      width: '100%',
                      background: isSelected ? dropdownItemActive : 'transparent',
                      border: 'none',
                      borderRadius: 7,
                      padding: '8px 10px',
                      fontSize: 13,
                      color: isSelected ? (isDark ? '#F0F0F0' : '#1A1A1A') : (isDark ? '#C0C0C0' : '#4A4A4A'),
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontFamily: 'inherit',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={e => {
                      if (!isSelected) e.currentTarget.style.background = dropdownItemHover
                    }}
                    onMouseLeave={e => {
                      if (!isSelected) e.currentTarget.style.background = 'transparent'
                    }}
                  >
                    <span style={{ fontSize: 16, lineHeight: 1 }}>{c.icon}</span>
                    <span style={{ flex: 1 }}>{c.label}</span>
                    {/* Connected status badge */}
                    {isConnected && (
                      <span style={{
                        fontSize: 10, fontWeight: 600,
                        color: '#22C55E',
                        background: 'rgba(34,197,94,0.1)',
                        border: '1px solid rgba(34,197,94,0.25)',
                        borderRadius: 4,
                        padding: '1px 6px',
                      }}>
                        Koblet
                      </span>
                    )}
                    {isSelected && (
                      <Check size={13} color="#1A93FE" />
                    )}
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '9px 20px',
              borderRadius: 9,
              border: `1px solid ${cancelBorder}`,
              background: 'transparent',
              color: cancelColor,
              fontSize: 13,
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontWeight: 500,
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = cancelHover)}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >
            Avbryt
          </button>
          <button
            onClick={handleCreate}
            disabled={!name.trim()}
            style={{
              padding: '9px 20px',
              borderRadius: 9,
              border: 'none',
              background: name.trim() ? createBg : createBgDisabled,
              color: name.trim() ? createColor : createColorDisabled,
              fontSize: 13,
              cursor: name.trim() ? 'pointer' : 'not-allowed',
              fontFamily: 'inherit',
              fontWeight: 500,
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => { if (name.trim()) e.currentTarget.style.background = createHover }}
            onMouseLeave={e => { if (name.trim()) e.currentTarget.style.background = createBg }}
          >
            Opprett
          </button>
        </div>
      </div>
    </div>
  )
}
